import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useSessionState } from "@/hooks/useSessionState";
import {
  fetchCollections,
  saveCollection,
  deleteCollection,
  updateCollectionBookPositions,
} from "@/api/collections";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import {
  Edit,
  Trash,
  Search,
  ChevronDown,
  GripVertical,
  BookMarked,
  Info,
  Plus,
  Eye,
  ArrowUpDown,
  Tag,
  Check,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Collection } from "@/types";
import { cn } from "@/lib/utils";
import { useToast } from "@/components/ui/use-toast";
import React from "react";

// Interface for collections API response
interface CollectionsResponse {
  data: Collection[];
  total: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Get a color for collection based on name
const getCollectionColor = (collectionTitle: string): string => {
  // Generate a consistent color based on the collection title
  const colors = [
    "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
    "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
    "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
    "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300",
    "bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300",
    "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
    "bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300",
    "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300",
  ];

  // Create a hash of the collection title to pick a consistent color
  let hash = 0;
  for (let i = 0; i < collectionTitle.length; i++) {
    hash = collectionTitle.charCodeAt(i) + ((hash << 5) - hash);
  }
  hash = Math.abs(hash);

  return colors[hash % colors.length];
};

interface SortableCollectionRowProps {
  collection: Collection;
  onEdit: (id: number) => void;
  onDelete: (id: number, title: string) => void;
  onToggleActive: (id: number, isActive: boolean) => void;
  showDetails: boolean;
  sortBy: "title" | "id" | "books" | "position" | "createdAt";
}

function SortableCollectionRow({
  collection,
  onEdit,
  onDelete,
  onToggleActive,
  showDetails,
  sortBy,
}: SortableCollectionRowProps) {
  const isDragEnabled = sortBy === "position";
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: collection.id,
    disabled: !isDragEnabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
  };

  const handleRowClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on a button or the drag handle
    if (
      (e.target as HTMLElement).closest("button") ||
      (e.target as HTMLElement).closest('[role="button"]') ||
      isDragging
    ) {
      return;
    }
    navigate(`/admin/collections/${collection.id}`);
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      className={`hover:bg-muted/50 ${
        isDragging ? "opacity-50 bg-accent" : ""
      } ${!collection.isActive ? "opacity-60" : ""} cursor-pointer`}
      onClick={handleRowClick}
    >
      {showDetails && (
        <TableCell className="font-mono text-sm">{collection.id}</TableCell>
      )}
      <TableCell>
        <div className="flex items-center">
          <div
            {...(isDragEnabled ? attributes : {})}
            {...(isDragEnabled ? listeners : {})}
            className={`${
              isDragEnabled
                ? "cursor-grab active:cursor-grabbing"
                : "cursor-not-allowed opacity-30"
            } p-1 mr-2`}
          >
            <GripVertical className="h-5 w-5 text-muted-foreground" />
          </div>
          <Badge variant="outline" className="mr-2">
            {collection.position !== undefined && collection.position !== null
              ? collection.position
              : "—"}
          </Badge>
        </div>
      </TableCell>
      <TableCell className="font-medium">
        <div className="flex items-center">
          <div
            className={cn(
              "flex items-center justify-center w-8 h-8 rounded-md mr-2",
              getCollectionColor(collection.title)
            )}
          >
            {collection.coverImage ? (
              <img
                src={collection.coverImage}
                alt={collection.title}
                className="w-7 h-7 rounded object-cover"
              />
            ) : (
              <BookMarked className="h-4 w-4" />
            )}
          </div>
          <span>{collection.title}</span>
          {!collection.isActive && (
            <Badge variant="outline" className="ml-2 text-muted-foreground">
              Hidden
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="secondary">{collection.books.length || 0}</Badge>
      </TableCell>
      <TableCell>
        {collection.createdAt ? (
          new Date(collection.createdAt).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center justify-center gap-2">
          <Switch
            checked={collection.isActive}
            onCheckedChange={(checked) =>
              onToggleActive(collection.id, checked)
            }
            aria-label={
              collection.isActive
                ? "Collection is active"
                : "Collection is hidden"
            }
          />
        </div>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <ChevronDown className="h-4 w-4" />
                <span className="sr-only">Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-popover border-border"
            >
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(collection.id);
                }}
                className="cursor-pointer"
              >
                <Edit className="mr-2 h-4 w-4" />
                Edit collection
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  );
}

const CollectionsList = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();

  // State for filters and pagination
  const [searchInput, setSearchInput] = useSessionState("collectionsList.searchInput", "");
  const [filters, setFilters] = useSessionState("collectionsList.filters", {
    search: "",
    active: undefined as boolean | undefined,
    sortBy: "position" as
      | "position"
      | "title"
      | "id"
      | "books"
      | "createdAt"
      | string,
  });
  const [showDetails, setShowDetails] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  // Removed analytics overview state for performance
  const [sortDirection, setSortDirection] = useSessionState<"asc" | "desc">("collectionsList.sortDirection", "asc");
  const [currentPage, setCurrentPage] = useSessionState("collectionsList.currentPage", 1);
  const [pageSize, setPageSize] = useSessionState("collectionsList.pageSize", 50);
  // Add local collections state for better UI handling during drag operations
  const [localCollections, setLocalCollections] = useState<Collection[]>([]);

  // Debounce search input to avoid too many requests
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        search: searchInput,
      }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Use sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Only start dragging after moving 8px to prevent accidental drags
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch collections data
  const {
    data: collectionsData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["collections-listing", filters, currentPage, pageSize],
    queryFn: () =>
      fetchCollections({
        page: currentPage,
        pageSize,
        filters,
      }),
  });

  // Initialize local collections when data is fetched
  useEffect(() => {
    if (collectionsData?.data) {
      setLocalCollections(collectionsData.data);
    }
  }, [collectionsData]);

  // Use local collections for UI rendering to maintain drag state
  const displayCollections = localCollections.length
    ? localCollections
    : collectionsData?.data || [];

  // Handle drag end for reordering
  const handleDragEnd = async (event: DragEndEvent) => {
    // Skip processing if not sorting by position
    if (!filters.sortBy.startsWith("position")) return;

    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    // Get the current collections to work with
    const currentCollections = [...displayCollections];

    if (currentCollections.length === 0) return;

    // Find the indexes
    const oldIndex = currentCollections.findIndex(
      (coll) => coll.id === Number(active.id)
    );
    const newIndex = currentCollections.findIndex(
      (coll) => coll.id === Number(over.id)
    );

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    try {
      // Create a new array with the reordered items
      const newCollectionsOrder = arrayMove(
        currentCollections,
        oldIndex,
        newIndex
      );

      // Update positions based on the new order
      const updatedCollections = newCollectionsOrder.map((coll, index) => ({
        ...coll,
        position: index,
      }));

      // Update local state immediately for a responsive UI
      setLocalCollections(updatedCollections);

      // Update positions on the server for each collection
      for (const coll of updatedCollections) {
        const originalCollection = currentCollections.find(
          (c) => c.id === coll.id
        );
        if (coll.position !== originalCollection?.position) {
          // Only update if position has changed
          await saveCollection({
            ...coll,
          });
          console.log(
            `Updated collection ${coll.id} position to ${coll.position}`
          );
        }
      }

      toast({
        title: "Collections reordered",
        description: "The collection order has been updated",
      });
    } catch (error) {
      console.error("Error reordering collections:", error);

      // Revert local state on error
      if (collectionsData?.data) {
        setLocalCollections(collectionsData.data);
      }

      // Revert the optimistic update on error
      queryClient.invalidateQueries({ queryKey: ["collections-listing"] });

      toast({
        title: "Error updating order",
        description: "There was a problem updating the collection order",
        variant: "destructive",
      });
    }
  };

  // Filter change handler
  const handleFilterChange = (
    name: string,
    value: string | boolean | undefined
  ) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Reset page when filter changes
    setCurrentPage(1);
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchInput("");
    setFilters({
      search: "",
      active: undefined,
      sortBy: "position",
    });
    setCurrentPage(1);
  };

  // Handle sorting by column
  const handleSort = (
    column: keyof Collection | "books" | "id" | "createdAt"
  ) => {
    if (filters.sortBy.startsWith(column.toString())) {
      // Toggle direction if already sorting by this column
      const isCurrentlyAsc = !filters.sortBy.includes("_desc");
      const newDirection = isCurrentlyAsc ? "desc" : "asc";
      setSortDirection(newDirection);
      handleFilterChange(
        "sortBy",
        `${column}${newDirection === "desc" ? "_desc" : ""}`
      );
    } else {
      // Start with ascending sort for new column
      setSortDirection("asc");
      handleFilterChange("sortBy", column.toString());
    }
  };

  // Get sort indicator for table header
  const getSortIndicator = (column: string) => {
    if (filters.sortBy.startsWith(column)) {
      return filters.sortBy.includes("_desc") ? (
        <ArrowUpDown className="h-4 w-4 ml-1 transform rotate-180" />
      ) : (
        <ArrowUpDown className="h-4 w-4 ml-1" />
      );
    }
    return null;
  };

  // Toggle collection active state
  const handleToggleActive = async (id: number, isActive: boolean) => {
    try {
      // Optimistically update UI
      queryClient.setQueryData(
        ["collections-listing", filters, currentPage, pageSize],
        (oldData: CollectionsResponse | undefined) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            data: oldData.data.map((collection: Collection) =>
              collection.id === id ? { ...collection, isActive } : collection
            ),
          };
        }
      );

      // Prepare collection data for API
      const collectionToUpdate = collectionsData?.data.find((c) => c.id === id);

      if (!collectionToUpdate) {
        throw new Error("Collection not found");
      }

      // Call API to update collection
      await saveCollection({
        ...collectionToUpdate,
        isActive,
      });

      // Show success message
      toast({
        title: isActive ? "Collection activated" : "Collection hidden",
        description: `"${collectionToUpdate.title}" is now ${
          isActive ? "active" : "hidden"
        }`,
      });
    } catch (error) {
      console.error("Error toggling collection status:", error);

      // Revert on error
      queryClient.invalidateQueries({ queryKey: ["collections-listing"] });

      toast({
        title: "Error updating collection",
        description: "There was a problem updating the collection status",
        variant: "destructive",
      });
    }
  };

  // Navigate to edit collection page
  const handleEditCollection = (id: number) => {
    navigate(`/admin/collections/${id}/edit`);
  };

  // Handle collection deletion
  const handleDeleteCollection = async (id: number, title: string) => {
    try {
      // Call API to delete collection
      await deleteCollection(id);

      // Update local data
      queryClient.invalidateQueries({ queryKey: ["collections-listing"] });

      toast({
        title: "Collection deleted",
        description: `The collection "${title}" has been deleted`,
      });
    } catch (error) {
      console.error("Error deleting collection:", error);

      toast({
        title: "Error deleting collection",
        description: "There was a problem deleting the collection",
        variant: "destructive",
      });
    }
  };

  // Pagination helpers
  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  const goToNextPage = () => {
    if (collectionsData && currentPage < collectionsData.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Get pagination range
  const getPaginationItems = () => {
    const items = [];
    const totalPages = collectionsData?.totalPages || 1;

    let startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, startPage + 4);

    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(i);
    }

    return items;
  };

  // Removed analytics calculations

  // Toggle show hidden collections
  const toggleShowHidden = () => {
    setShowHidden(!showHidden);
    handleFilterChange("active", !showHidden ? undefined : true);
  };

  if (isLoading) {
    return (
      <AdminLayout title="Collections">
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  // Add collection button for the action button in AdminLayout
  const addCollectionButton = (
    <Button onClick={() => navigate("/admin/collections/new")}>
      <Plus className="mr-2 h-4 w-4" />
      Add Collection
    </Button>
  );

  return (
    <AdminLayout title="Collections" actionButton={addCollectionButton}>
      <div className="h-full flex flex-col space-y-6">
        {/* Analytics Overview removed for performance */}

        {/* Search and Filter Card */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              Search and filter collections
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="relative flex-1 w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search collections..."
                  className="pl-10"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>

              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2 whitespace-nowrap">
                  <Switch
                    id="show-inactive"
                    checked={showHidden}
                    onCheckedChange={toggleShowHidden}
                  />
                  <Label htmlFor="show-inactive" className="cursor-pointer">
                    Show hidden collections
                  </Label>
                </div>

                <div className="flex items-center space-x-2 whitespace-nowrap">
                  <Switch
                    id="show-details"
                    checked={showDetails}
                    onCheckedChange={setShowDetails}
                  />
                  <Label htmlFor="show-details" className="cursor-pointer">
                    Show ID
                  </Label>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Drag instruction alert */}
        {!filters.sortBy.startsWith("position") && (
          <Alert className="mb-6">
            <Info className="h-4 w-4 mr-2" />
            <AlertDescription>
              Drag and drop reordering is only available when sorting by{" "}
              <strong>Position</strong>. Click on the "Position" column header
              to enable drag and drop functionality.
            </AlertDescription>
          </Alert>
        )}

        {/* Collections Table */}
        <Card>
          <div className="rounded-md border border-border">
            <div className="relative w-full overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-muted/50">
                    {showDetails && (
                      <TableHead
                        className="cursor-pointer w-[100px]"
                        onClick={() => handleSort("id")}
                      >
                        <div className="flex items-center space-x-1">
                          <span>ID</span>
                          {getSortIndicator("id")}
                        </div>
                      </TableHead>
                    )}
                    <TableHead
                      className="cursor-pointer w-[120px]"
                      onClick={() => handleSort("position")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Position</span>
                        {getSortIndicator("position")}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("title")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Title</span>
                        {getSortIndicator("title")}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("books")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Books</span>
                        {getSortIndicator("books")}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("createdAt")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Created</span>
                        {getSortIndicator("createdAt")}
                      </div>
                    </TableHead>
                    <TableHead className="text-center w-[100px]">
                      <div className="flex items-center justify-center">
                        <Eye className="h-4 w-4 mr-1" />
                        <span>Status</span>
                      </div>
                    </TableHead>
                    <TableHead className="text-right w-[100px]">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell
                        colSpan={showDetails ? 7 : 6}
                        className="h-24 text-center"
                      >
                        <div className="flex justify-center">
                          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : isError ? (
                    <TableRow>
                      <TableCell
                        colSpan={showDetails ? 7 : 6}
                        className="h-24 text-center text-muted-foreground"
                      >
                        Error loading collections
                      </TableCell>
                    </TableRow>
                  ) : displayCollections.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={showDetails ? 7 : 6}
                        className="h-24 text-center text-muted-foreground"
                      >
                        <div className="flex flex-col items-center justify-center">
                          <Tag className="h-8 w-8 mb-2 text-muted-foreground/50" />
                          <h3 className="font-medium">No collections found</h3>
                          <p className="text-sm text-muted-foreground mt-1">
                            {searchInput
                              ? `No collections match "${searchInput}"`
                              : "There are no collections defined yet."}
                          </p>
                          {searchInput && (
                            <Button
                              variant="outline"
                              onClick={() => setSearchInput("")}
                              className="mt-2"
                              size="sm"
                            >
                              Clear Search
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={displayCollections.map((c) => c.id)}
                        strategy={verticalListSortingStrategy}
                        disabled={!filters.sortBy.startsWith("position")}
                      >
                        {displayCollections.map((collection) => (
                          <SortableCollectionRow
                            key={collection.id}
                            collection={collection}
                            onEdit={handleEditCollection}
                            onDelete={handleDeleteCollection}
                            onToggleActive={handleToggleActive}
                            showDetails={showDetails}
                            sortBy={
                              filters.sortBy.split("_")[0] as
                                | "title"
                                | "id"
                                | "books"
                                | "position"
                                | "createdAt"
                            }
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </Card>

        {/* Pagination */}
        {collectionsData && collectionsData.totalPages > 1 && (
          <div className="flex items-center justify-between space-x-2 py-4">
            <div className="text-sm text-muted-foreground">
              Showing {(currentPage - 1) * pageSize + 1} to{" "}
              {Math.min(currentPage * pageSize, collectionsData.total)} of{" "}
              {collectionsData.total} collections
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              {getPaginationItems().map((page) => (
                <Button
                  key={page}
                  variant={currentPage === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => goToPage(page)}
                  className="w-9"
                >
                  {page}
                </Button>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={goToNextPage}
                disabled={
                  currentPage === collectionsData.totalPages ||
                  collectionsData.totalPages === 0
                }
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default CollectionsList;
